import React, { useEffect, useRef, useState } from 'react';
import { Trash2, GripVertical } from 'lucide-react';

/**
 * Draggable + resizable wrapper for a single overlay widget on top of the
 * video. Coordinates are stored as percentages of the parent container so the
 * layout scales with the player size.
 *
 * Props:
 *   widget: { id, x, y, w, h, color, visible }
 *   containerRef: ref to the absolutely-positioned bounding container
 *   onChange: (partial) => void   — persisted by the parent
 *   onRemove: () => void
 *   editing: boolean              — show drag/resize handles + delete button
 *   children: contents
 */
export default function OverlayWidget({
  widget, containerRef, onChange, onRemove, editing, children, label, locked,
}) {
  const ref = useRef(null);
  const stateRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  // Convert px→% relative to the parent container so widgets scale with it.
  const pctOf = (dxPx, dyPx) => {
    const c = containerRef.current;
    if (!c) return { dx: 0, dy: 0 };
    const r = c.getBoundingClientRect();
    return { dx: (dxPx / r.width) * 100, dy: (dyPx / r.height) * 100 };
  };

  const startDrag = (mode, ev) => {
    if (!editing) return;
    ev.preventDefault();
    ev.stopPropagation();
    const startX = ev.touches ? ev.touches[0].clientX : ev.clientX;
    const startY = ev.touches ? ev.touches[0].clientY : ev.clientY;
    stateRef.current = { mode, startX, startY, x: widget.x, y: widget.y, w: widget.w, h: widget.h };
    setDragging(true);

    const onMove = (mv) => {
      const cx = mv.touches ? mv.touches[0].clientX : mv.clientX;
      const cy = mv.touches ? mv.touches[0].clientY : mv.clientY;
      const { dx, dy } = pctOf(cx - stateRef.current.startX, cy - stateRef.current.startY);
      if (stateRef.current.mode === 'move') {
        onChange({
          x: Math.max(0, Math.min(100 - widget.w, stateRef.current.x + dx)),
          y: Math.max(0, Math.min(100 - widget.h, stateRef.current.y + dy)),
        });
      } else {
        onChange({
          w: Math.max(8, Math.min(100 - widget.x, stateRef.current.w + dx)),
          h: Math.max(6, Math.min(100 - widget.y, stateRef.current.h + dy)),
        });
      }
    };
    const onUp = () => {
      setDragging(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);
  };

  if (!widget.visible) return null;

  return (
    <div
      ref={ref}
      data-testid={`overlay-widget-${widget.id}`}
      className={`absolute ${editing ? 'pointer-events-auto cursor-move' : 'pointer-events-none'} ${dragging ? 'opacity-90' : ''}`}
      style={{
        left: `${widget.x}%`,
        top: `${widget.y}%`,
        width: `${widget.w}%`,
        height: `${widget.h}%`,
        outline: editing ? '1px dashed rgba(255,255,255,0.35)' : 'none',
        outlineOffset: editing ? 2 : 0,
      }}
      onMouseDown={(e) => startDrag('move', e)}
      onTouchStart={(e) => startDrag('move', e)}
    >
      <div className="w-full h-full">
        {children}
      </div>
      {editing && (
        <>
          <div className="absolute -top-5 left-0 right-0 flex items-center gap-1 text-[10px] text-white pointer-events-none">
            <GripVertical className="w-3 h-3 opacity-60" />
            <span className="opacity-80 truncate">{label}</span>
            {!locked && (
              <button
                type="button"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onRemove(); }}
                className="ml-auto pointer-events-auto bg-red-600/80 hover:bg-red-500 rounded p-0.5"
                data-testid={`overlay-widget-${widget.id}-remove`}
                title="Quitar"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
          {/* Resize handle bottom-right */}
          <div
            onMouseDown={(e) => startDrag('resize', e)}
            onTouchStart={(e) => startDrag('resize', e)}
            className="absolute -right-1 -bottom-1 w-3 h-3 bg-white/80 border border-black/40 rounded-sm cursor-nwse-resize pointer-events-auto"
            data-testid={`overlay-widget-${widget.id}-resize`}
          />
        </>
      )}
    </div>
  );
}
